import { MutableRefObject, useRef } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import {
  EDITOR_DOUBLE_CLICK_INTERVAL,
  EDITOR_DOUBLE_CLICK_POS_THRESHOLD,
  EDITOR_DRAG_DETECTION_THRESHOLD,
  EDITOR_MOUSE_DRAG_THRESHOLD,
  EDITOR_TOOLS,
} from '@editor/config/constants'
import { CursorState } from '@editor/config/constants/cursor'
import { useEditorContext } from '@editor/contexts/Editor'
import { CanvasConfig } from '@editor/interfaces/editor'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { setCurrentAnchorPosition, setCursor, setIsDragging } from '@editor/store/tool'
import { useLocation } from 'react-router'
import { useThrottledCallback } from 'use-debounce'

import usePointSelection from './usePointSelection'

const useCanvasEvents = (
  pointClouds: PointCloudOctree[],
  toolsHook: MutableRefObject<
    {
      key: string
      config: CanvasConfig
    }[]
  >,
) => {
  // URL Params
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Contexts
  const { changeTool, selectedTool, prevSelectedTool, cameraRef, arcballControlsRef } = useEditorContext()

  // Hooks
  const { getSelectedPoint } = usePointSelection(pointClouds)

  // Store
  const dispatch = useAppDispatch()
  const isDragging = useAppSelector((state) => state.toolUtil.core.isDragging)
  const cursor = useAppSelector((state) => state.toolUtil.core.cursor)
  const disableRotation = useAppSelector((state) => state.editor.disableRotation)
  const editingElementId = useAppSelector((state) => state.toolUtil.core.editingElementId)

  // Refs
  const isMouseMoving = useRef(false)
  const isPointerDown = useRef(false)
  const startDraggingPoint = useRef<{ x: number; y: number } | null>(null)

  const tripleClickInitiatedTimes = useRef<Date[]>([])
  const lastMouseEvent = useRef<React.MouseEvent<HTMLDivElement, MouseEvent> | undefined>(undefined)

  const tripleClickInitiatedTimesTouch = useRef<Date[]>([])
  const lastTouchEvent = useRef<React.TouchEvent<HTMLDivElement> | undefined>(undefined)

  // Drag detections
  const dragDistance = useRef<number>(0)
  const lastPointerPosition = useRef<{ x: number; y: number } | null>(null)

  /**
   * Accumulate drag distance
   *
   * @param currentPointerPosition - current pointer position
   * @returns current pointer position
   */
  const accumulateDragDistance = (currentPointerPosition: { x: number; y: number }): { x: number; y: number } => {
    if (isPointerDown.current && lastPointerPosition.current) {
      const deltaX = currentPointerPosition.x - lastPointerPosition.current.x
      const deltaY = currentPointerPosition.y - lastPointerPosition.current.y
      dragDistance.current += Math.sqrt(deltaX * deltaX + deltaY * deltaY)
    }
    lastPointerPosition.current = currentPointerPosition
    return currentPointerPosition
  }

  /**
   * Canvas touch start event handlers
   * @param e
   */
  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!inspection_area_id) return

    // -- Initialize drag detection
    dragDistance.current = 0
    isPointerDown.current = true
    lastPointerPosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }

    // -- Get point on PCD/objects.
    const anchorPoint = getSelectedPoint(undefined, e)

    // -- Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onTouchStart && tool.config.events.onTouchStart(e, anchorPoint?.[0], anchorPoint?.[1]),
    )
  }

  /**
   * Canvas touch move event handlers
   * @param e
   */
  const onTouchMoveCapture = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!inspection_area_id) return

    // -- Accumulate drag distance
    lastPointerPosition.current = accumulateDragDistance({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    })

    // -- Check if we can get point on PCD/objects.
    const getPoint = !(selectedTool === EDITOR_TOOLS.MOVE || !((isDragging && editingElementId) || disableRotation))
    const anchorPoint = getPoint ? getSelectedPoint(undefined, e) : undefined

    if (anchorPoint) {
      dispatch(setCurrentAnchorPosition(anchorPoint[0] || anchorPoint[1]))
    }

    // -- Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onTouchMoveCapture &&
        tool.config.events.onTouchMoveCapture(e, anchorPoint?.[0], anchorPoint?.[1]),
    )
  }

  /**
   * Canvas touch end event handlers
   * @param e
   */
  const onTouchEnd = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!inspection_area_id) return

    // -- Check for double/triple click
    const isClickedNearPoint =
      lastTouchEvent.current !== undefined &&
      Math.abs(lastTouchEvent.current.changedTouches[0].clientX - e.changedTouches[0].clientX) <
        EDITOR_DOUBLE_CLICK_POS_THRESHOLD &&
      Math.abs(lastTouchEvent.current.changedTouches[0].clientY - e.changedTouches[0].clientY) <
        EDITOR_DOUBLE_CLICK_POS_THRESHOLD

    // re-using existing triple click logic to determine double/triple click
    const isDoubleClicked =
      tripleClickInitiatedTimesTouch.current.length === 1 &&
      Date.now() - tripleClickInitiatedTimesTouch.current[0].valueOf() < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint
    const isTripleClicked =
      tripleClickInitiatedTimesTouch.current.length === 2 &&
      Math.abs(tripleClickInitiatedTimesTouch.current[1].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint

    if (isTripleClicked) {
      tripleClickInitiatedTimesTouch.current = []
    } else if (
      tripleClickInitiatedTimesTouch.current.length === 1 &&
      Math.abs(tripleClickInitiatedTimesTouch.current[0].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL
    ) {
      tripleClickInitiatedTimesTouch.current = [tripleClickInitiatedTimesTouch.current[0], new Date()]
    } else {
      tripleClickInitiatedTimesTouch.current = [new Date()]
    }

    // -- Check for canvas drag
    const isDragged = dragDistance.current > EDITOR_DRAG_DETECTION_THRESHOLD

    // -- Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onTouchEnd &&
        tool.config.events.onTouchEnd(e, { isDoubleClicked, isTripleClicked, isDragged }),
    )

    // -- Reset states
    dispatch(setIsDragging(false))
    lastTouchEvent.current = e
    dragDistance.current = 0
  }

  /**
   * Canvas mouse down event handlers
   * @param mouseEvent
   */
  const onMouseDown = (mouseEvent: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!inspection_area_id) return

    // -- Initialize drag detection
    dragDistance.current = 0
    lastPointerPosition.current = { x: mouseEvent.clientX, y: mouseEvent.clientY }
    isPointerDown.current = mouseEvent.button === 0 // drag check only for left button
    isMouseMoving.current = false
    startDraggingPoint.current = {
      x: mouseEvent.clientX,
      y: mouseEvent.clientY,
    }

    if (selectedTool === EDITOR_TOOLS.MOVE) return

    // -- Get point on PCD/objects.
    const anchorPoint = getSelectedPoint(mouseEvent)

    // -- Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onMouseDown &&
        tool.config.events.onMouseDown(mouseEvent, anchorPoint?.[0], anchorPoint?.[1]),
    )
  }

  /**
   * Canvas mouse move event handlers
   * @param mouseEvent
   * @param touchEvent
   */
  const onMouseMove = useThrottledCallback(
    (mouseEvent?: React.MouseEvent<HTMLDivElement, MouseEvent>, touchEvent?: React.TouchEvent<HTMLDivElement>) => {
      // -- Accumulate drag distance
      lastPointerPosition.current = accumulateDragDistance({
        x: mouseEvent?.clientX ?? touchEvent?.touches[0]?.clientX ?? 0,
        y: mouseEvent?.clientY ?? touchEvent?.touches[0]?.clientY ?? 0,
      })

      // -- Check if we can get point on PCD/objects.
      const getPoint =
        !(
          selectedTool === EDITOR_TOOLS.MOVE ||
          (isPointerDown.current && !((isDragging && editingElementId) || disableRotation))
        ) && !touchEvent

      const anchorPoint = getPoint ? getSelectedPoint(mouseEvent, touchEvent) : undefined
      if (anchorPoint) {
        dispatch(setCurrentAnchorPosition(anchorPoint[0] || anchorPoint[1]))
      }

      // -- Execute events for tools
      toolsHook.current.forEach(
        (tool) =>
          tool.config?.events?.onMove &&
          tool.config.events.onMove(anchorPoint?.[0], anchorPoint?.[1], mouseEvent, touchEvent, {
            isDoubleClicked: false,
            isTripleClicked: false,
            isDragged: false,
            sampleSolidMeshColor: anchorPoint?.[2],
          }),
      )
    },
    1000 / 30,
  )
  /**
   * Canvas mouse up event handlers
   * @param e
   */
  const onMouseUp = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!inspection_area_id) return

    // -- Reset states
    isPointerDown.current = false

    // -- Get point on PCD/objects.
    const anchorPoint = getSelectedPoint(e)

    // -- Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onRawMouseUp && tool.config.events.onRawMouseUp(e, anchorPoint?.[0], anchorPoint?.[1]),
    )

    if (e.button !== 0) return

    // -- Check for canvas drag
    const isDragged = dragDistance.current > EDITOR_DRAG_DETECTION_THRESHOLD

    const isClickedNearPoint =
      lastMouseEvent.current !== undefined &&
      Math.abs(lastMouseEvent.current.clientX - e.clientX) < EDITOR_DOUBLE_CLICK_POS_THRESHOLD &&
      Math.abs(lastMouseEvent.current.clientY - e.clientY) < EDITOR_DOUBLE_CLICK_POS_THRESHOLD
    const isDoubleClicked =
      tripleClickInitiatedTimes.current.length === 1 &&
      Date.now() - tripleClickInitiatedTimes.current[0].valueOf() < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint
    const isTripleClicked =
      tripleClickInitiatedTimes.current.length === 2 &&
      Math.abs(tripleClickInitiatedTimes.current[1].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL &&
      isClickedNearPoint
    lastMouseEvent.current = e
    if (isTripleClicked) {
      tripleClickInitiatedTimes.current = []
    } else if (
      tripleClickInitiatedTimes.current.length === 1 &&
      Math.abs(tripleClickInitiatedTimes.current[0].valueOf() - Date.now()) < EDITOR_DOUBLE_CLICK_INTERVAL
    ) {
      tripleClickInitiatedTimes.current = [tripleClickInitiatedTimes.current[0], new Date()]
    } else {
      tripleClickInitiatedTimes.current = [new Date()]
    }

    // Execute events for tools
    toolsHook.current.forEach(
      (tool) =>
        tool.config?.events?.onMouseUp &&
        tool.config.events.onMouseUp(e, anchorPoint?.[0], anchorPoint?.[1], {
          isTripleClicked,
          isDoubleClicked,
          isDragged,
          sampleSolidMeshColor: anchorPoint?.[2],
        }),
    )

    // -- Reset states
    dispatch(setIsDragging(false))
    dragDistance.current = 0

    // If the user was grabbing something, onUp they will still be on whatever they grabbed.
    // So instead of resetting to default state, set it back to grab.
    if (cursor === CursorState.GRABBING) dispatch(setCursor(CursorState.GRAB))
    const currentPoint = {
      x: e?.clientX,
      y: e?.clientY,
    }
    const distance = isMouseMoving.current
      ? (startDraggingPoint.current!.x - currentPoint.x) ** 2 + (startDraggingPoint.current!.y - currentPoint.y) ** 2
      : 0
    if (!isMouseMoving.current || distance < EDITOR_MOUSE_DRAG_THRESHOLD) {
      isMouseMoving.current = false
      if (selectedTool === EDITOR_TOOLS.FOCUS && cameraRef.current && arcballControlsRef.current) {
        changeTool(prevSelectedTool)
      }
    }
  }

  return {
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onTouchStart,
    onTouchMoveCapture,
    onTouchEnd,
  }
}

export default useCanvasEvents
